import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useDisconnect } from 'wagmi'
import { FormikProps } from 'formik'

import {
  BuyerActions,
  CampaignPublicActions,
  WalletActions,
  campaignPublicSelector,
  isLoadingCampaignPublicSelector,
  mediumCampaignPublicImageSelector,
  originalCampaignPublicImageSelector,
  useModal,
  walletAddressSelector,
} from 'primsell-shared-layer/features'

import { IBurnFormValues, ModalVariantE } from 'types'

const useRedeemCampaignController = () => {
  const [isOpenConnectDialog, setIsOpenConnectDialog] = useState(false)

  const dispatch = useDispatch()
  const { code } = useParams()

  const { openModal } = useModal()

  const campaignPublicDetails = useSelector(campaignPublicSelector)
  const isLoadingCampaignPublic = useSelector(isLoadingCampaignPublicSelector)
  const walletAddress = useSelector(walletAddressSelector)
  const mediumPublicCampaignImage = useSelector(mediumCampaignPublicImageSelector)
  const originalPublicCampaignImage = useSelector(originalCampaignPublicImageSelector)

  const formikExternalRef = useRef<FormikProps<IBurnFormValues>>(null)

  const isConnectButtonDisabled: boolean = useMemo(() => {
    if (!campaignPublicDetails?.startDate) {
      return true
    }
    const startDate = new Date(campaignPublicDetails?.startDate)

    const currentDate = new Date()

    return startDate.getTime() > currentDate.getTime()
  }, [campaignPublicDetails?.startDate])

  const handleSubmitBurnForm = () => {
    if (campaignPublicDetails?.code && campaignPublicDetails?.contractAddress && walletAddress) {
      dispatch(
        WalletActions.getWalletBalanceRequest({
          contractAddress: campaignPublicDetails?.contractAddress,
          address: walletAddress,
          code: campaignPublicDetails?.code,
        })
      )
    }
    openModal({ variant: ModalVariantE.BURN_CAMPAIGN_NFT })
  }

  const { disconnect } = useDisconnect({
    onSuccess() {
      dispatch(BuyerActions.clearBuyerData())
    },
  })

  const handleDisconnect = useCallback(() => {
    disconnect()
    formikExternalRef?.current?.resetForm()
  }, [disconnect])

  useEffect(() => {
    if (code) {
      dispatch(CampaignPublicActions.getPublicCampaignDetailsByCodeRequest({ code }))
    }
  }, [code, dispatch])

  return {
    isConnectButtonDisabled,
    formikExternalRef,
    isLoadingCampaignPublic,
    campaignPublicDetails,
    mediumPublicCampaignImage,
    originalPublicCampaignImage,
    isOpenConnectDialog,
    setIsOpenConnectDialog,
    handleSubmitBurnForm,
    handleDisconnect,
  }
}

export { useRedeemCampaignController }
